/**
 * InspectionWorkflowVerify - Step 2 of the inspection completion workflow.
 *
 * Mirrors WorkflowVerify's outer chrome and pacing but tailors the
 * summary surface for inspection ratings rather than binary completion:
 * each row shows the chosen rating as a colored chip and any
 * dissatisfied note inline beneath it so the inspector can audit their
 * findings before committing.
 *
 * Photos captured here are uploaded as general "Observation" photos
 * (not per-item). The upload itself is delegated to the parent because
 * photoTypeService and uploadService both live above the workflow tree.
 */
import React from 'react';
import { Alert, RatingBadge } from '../../atoms';
import type { ChecklistItem } from '../../../services/checklistService';
import type { ChecklistItemRating } from '../../../services/checklistInstanceService';
import type { CapturedPhoto } from '../PhotoCapture/PhotoCapture';
import PhotoCapture from '../PhotoCapture/PhotoCapture';
import Button from '../../atoms/Button/Button';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

export interface InspectionWorkflowVerifyProps {
	items: ChecklistItem[];
	verificationPhotos: CapturedPhoto[];
	onAddPhoto: (photo: CapturedPhoto) => void;
	onRemovePhoto: (index: number) => void;
	/** Step-2 commit. Parent uploads photos then advances to sign-off. */
	onSubmitVerification: () => Promise<void>;
	onBack: () => void;
	isSubmitting: boolean;
	error: string | null;
	/** Optional extra classes for the outer wrapper */
	className?: string;
}

/* ============================================
 * COMPONENT
 * ============================================ */

const InspectionWorkflowVerify: React.FC<InspectionWorkflowVerifyProps> = ({
	items,
	verificationPhotos,
	onAddPhoto,
	onRemovePhoto,
	onSubmitVerification,
	onBack,
	isSubmitting,
	error,
	className,
}) => {
	const sortedItems = [...items].sort((a, b) => a.sortOrder - b.sortOrder);

	return (
		<div className={`inspection-workflow-verify flex flex-col h-full ${className ?? ''}`} data-ref="organism-inspection-workflow-verify">
			{/* --- Scrollable content --- */}
			<div className="inspection-workflow-verify__body flex-1 overflow-y-auto px-16px pt-16px pb-16px">
				<div className="mb-20px">
					<div className="flex items-center gap-10px mb-8px">
						<div className="w-40px h-40px rounded-full bg-brand-primary/10 flex items-center justify-center">
							<svg className="w-22px h-22px text-brand-primary" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
								<path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z" />
							</svg>
						</div>
						<div>
							<h3 className="text-18px font-sans font-bold text-brand-text">
								Verify Observations
							</h3>
							<p className="text-13px font-sans text-brand-text-muted">
								Confirm your ratings and add any general observation photos
							</p>
						</div>
					</div>
				</div>

				{error && (
					<Alert variant="error" size="sm" icon className="mb-12px">{error}</Alert>
				)}

				{/* --- Rated items summary --- */}
				<div className="mb-20px">
					<h4 className="text-13px font-sans font-bold text-brand-text-muted uppercase tracking-wide mb-8px">
						Rated Items ({sortedItems.length})
					</h4>
					<div className="border border-brand-border rounded-lg p-12px bg-white">
						<ul className="flex flex-col gap-10px">
							{sortedItems.map((item) => {
								const rating = ((item as any).rating ?? null) as ChecklistItemRating | null;
								/* ratingNote already carries the retired
								   dissatisfied_note for historical rows (see the
								   checklistService adapter). */
								const note = item.ratingNote ?? '';
								return (
									<li key={item.id} className="inspection-workflow-verify__item flex flex-col gap-4px">
										<div className="flex items-start justify-between gap-8px">
											<span className="text-14px font-sans text-brand-text leading-snug">
												{item.title}
											</span>
											{/* Shared badge — same colors and wording as the
											    picker on Step 1 and the detail page. */}
											<RatingBadge rating={rating} size="sm" className="flex-shrink-0" />
										</div>
										{/* The reason matters most on the tiers that create
										    follow-up work, so surface it for both of them. */}
										{note && (rating === 'critical' || rating === 'needs_attention') && (
											<p className={[
												'text-12px font-sans italic rounded px-8px py-4px border',
												rating === 'critical'
													? 'text-red-700 bg-red-50 border-red-100'
													: 'text-amber-800 bg-amber-50 border-amber-100',
											].join(' ')}>
												{note}
											</p>
										)}
									</li>
								);
							})}
						</ul>
						{sortedItems.length === 0 && (
							<p className="text-13px font-sans text-brand-text-muted text-center py-12px">
								No checklist items to summarize.
							</p>
						)}
					</div>
				</div>

				{/* --- Observation photo capture --- */}
				<div className="mb-20px">
					<h4 className="text-13px font-sans font-bold text-brand-text-muted uppercase tracking-wide mb-8px">
						Observation Photos
					</h4>
					<p className="text-13px font-sans text-brand-text-muted mb-12px">
						Add general photos that document the overall site condition. These will be uploaded as observation photos when you continue.
					</p>

					{verificationPhotos.length > 0 && (
						<div className="grid grid-cols-3 gap-8px mb-12px">
							{verificationPhotos.map((photo, index) => (
								<div key={index} className="relative aspect-square rounded-lg overflow-hidden border border-gray-200">
									<img src={photo.base64} alt={`Observation ${index + 1}`} className="w-full h-full object-cover" />
									<button type="button" onClick={() => onRemovePhoto(index)} className="absolute top-4px right-4px w-44px h-44px desktop:w-36px desktop:h-36px rounded-full bg-black/60 flex items-center justify-center text-white hover:bg-black/80 transition-colors" aria-label={`Remove photo ${index + 1}`}>
										<svg className="w-16px h-16px" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" aria-hidden="true">
											<path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
										</svg>
									</button>
								</div>
							))}
						</div>
					)}

					<PhotoCapture onCapture={onAddPhoto} label="Add Observation Photo" />
				</div>
			</div>

			{/* --- Bottom action bar --- */}
			<div className="inspection-workflow-verify__footer px-16px pb-16px pt-8px border-t border-gray-100 flex flex-col gap-8px">
				<Button variant="primary" size="lg" fullWidth onClick={onSubmitVerification} isLoading={isSubmitting} disabled={isSubmitting}>
					Continue to Sign Off
				</Button>
				<Button variant="ghost" size="md" fullWidth onClick={onBack} disabled={isSubmitting}>
					Back to Checklist
				</Button>
			</div>
		</div>
	);
};

export default InspectionWorkflowVerify;
